import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./Scheduled.module.sass";
import Card from "../../../components/Card";
import Form from "../../../components/Form";
import Table from "./Table";
import Actions from "../../../components/Actions";
import ModalExportAttendance from "../../../components/ModalExportAttendance";

// Services
import { getAttendances, updateAttendance } from "../../../services/attendances.service";
import { createStudentMemo } from "../../../services/studentMemo.service";
import { useAuth } from "../../../contexts/AuthContext";

const Absences = ({ studentId }) => {
    const { currentUser } = useAuth();
    const [search, setSearch] = useState("");
    const [absences, setAbsences] = useState([]);
    const [filteredAbsences, setFilteredAbsences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exportModalVisible, setExportModalVisible] = useState(false);

    // Actions pour le menu
    const actions = [
        {
            title: "Exporter les présences",
            icon: "download",
            action: () => setExportModalVisible(true)
        }
    ];

    useEffect(() => {
        const fetchAbsences = async () => {
            try {
                setLoading(true);
                // Récupérer les attendances où l'élève est absent
                const result = await getAttendances({
                    filter: `participant_id = "${studentId}" && status = "Absent"`,
                    sort: '-created',
                    perPage: 100
                });
                
                // Transformer les absences pour correspondre au format attendu
                const formattedAbsences = result.items.map(attendance => {
                    const session = attendance.expand?.session_id;
                    const teacher = session?.expand?.teacher_id;
                    
                    return {
                        id: attendance.id,
                        sessionName: session?.title || 'Séance sans nom',
                        teacherName: teacher ? `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim() : 'Formateur non spécifié',
                        date: session?.start_date ? new Date(session.start_date).toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        }) : 'Date non spécifiée',
                        startTime: session?.start_date || '',
                        endTime: session?.end_date || '',
                        status: attendance.status,
                        justification: attendance.status_justification === 'Absence justifiée',
                        originalAttendance: attendance
                    };
                });
                
                setAbsences(formattedAbsences);
                setFilteredAbsences(formattedAbsences);
            } catch (error) {
                console.error('Erreur lors de la récupération des absences:', error);
            } finally {
                setLoading(false);
            }
        };

        if (studentId) {
            fetchAbsences();
        }
    }, [studentId]);

    // Fonction pour filtrer les absences en fonction du texte de recherche
    const filterAbsences = (searchText) => {
        if (!searchText.trim()) {
            setFilteredAbsences(absences);
            return;
        }
        
        const searchLower = searchText.toLowerCase();
        const filtered = absences.filter(absence => {
            const sessionMatch = absence.sessionName.toLowerCase().includes(searchLower);
            const teacherMatch = absence.teacherName.toLowerCase().includes(searchLower);
            const dateMatch = absence.date.toLowerCase().includes(searchLower);
            
            return sessionMatch || teacherMatch || dateMatch;
        });
        
        setFilteredAbsences(filtered);
    };
    
    // Effet pour filtrer les absences lorsque la recherche change
    useEffect(() => {
        filterAbsences(search);
    }, [search, absences]);
    
    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        filterAbsences(search);
    };

    // Fonction pour justifier une absence
    const handleJustify = async (item) => {
        try {
            await updateAttendance(item.id, { status_justification: 'Absence justifiée' });
            
            // Créer un mémo pour tracer la justification
            const today = new Date().toLocaleDateString('fr-FR');
            const memoData = {
                student_id: studentId,
                topic: `Absence justifiée - ${item.sessionName}`,
                content: JSON.stringify({
                    blocks: [{
                        key: 'memo',
                        text: `Absence du ${item.date} (${item.sessionName}) marquée comme justifiée le ${today}.`,
                        type: 'unstyled',
                        depth: 0,
                        inlineStyleRanges: [],
                        entityRanges: [],
                        data: {}
                    }],
                    entityMap: {}
                }),
                created_by: currentUser?.id
            };
            await createStudentMemo(memoData);
            
            // Mettre à jour l'état local
            setAbsences(prevAbsences => 
                prevAbsences.map(absence => 
                    absence.id === item.id 
                        ? { ...absence, justification: true }
                        : absence
                )
            );
        } catch (error) {
            console.error('Erreur lors de la justification:', error);
        }
    };

    // Fonction pour retirer la justification d'une absence
    const handleUnjustify = async (item) => {
        try {
            await updateAttendance(item.id, { status_justification: 'Absence injustifiée' });
            // Mettre à jour l'état local
            setAbsences(prevAbsences => 
                prevAbsences.map(absence => 
                    absence.id === item.id 
                        ? { ...absence, justification: false }
                        : absence
                )
            );
        } catch (error) {
            console.error('Erreur lors du retrait de la justification:', error);
        }
    };

    return (
        <Card
            className={styles.card}
            classCardHead={styles.head}
            title="Absences"
            classTitle={cn("title-red", styles.title)}
            head={
                <>
                    <Form
                        className={styles.form}
                        value={search}
                        setValue={setSearch}
                        onSubmit={handleSubmit}
                        placeholder="Rechercher par séance ou formateur"
                        type="text"
                        name="search"
                        icon="search"
                    />
                    <div className={styles.sorting}>
                        <Actions
                            className={styles.actions}
                            classActionsHead={styles.actionsHead}
                            items={actions}
                            small
                        />
                    </div>
                </>
            }
        >
            <div className={styles.wrapper}>
                {loading ? (
                    <div className={styles.loading}>Chargement des absences...</div>
                ) : filteredAbsences.length > 0 ? (
                    <Table 
                        items={filteredAbsences} 
                        onJustify={handleJustify}
                        onUnjustify={handleUnjustify}
                    />
                ) : absences.length > 0 ? (
                    <div className={styles.empty}>Aucune absence ne correspond à votre recherche</div>
                ) : (
                    <div className={styles.empty}>Aucune absence enregistrée pour cet élève</div>
                )}
            </div>

            <ModalExportAttendance
                visible={exportModalVisible}
                onClose={() => setExportModalVisible(false)}
                studentId={studentId}
            />
        </Card>
    );
};

export default Absences;
